import {
  Card,
  Field,
  Stack,
  Input,
  Separator,
  Text,
  Box,
  Flex,
  VStack,
  Spinner,
} from '@chakra-ui/react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { type User } from '@halo/chat-sdk-react';
import { get } from 'lodash';
import { useAppStore } from '@halo/chat-sdk-react';
import { useShallowEnhance } from '@halo/zustand-enhance';
import { InputSize } from '@halo/ui-chat-react';
import dayjs from 'dayjs';
import type { ActionType } from '@/scripts/hooks';
import { DatePicker } from '@/components/DatePicker';
import { EditGender } from '@/components/ProfileEditDialog/EditGender';
import type { UserState } from '../index';

interface Props {
  user: User;
  form: UseFormReturn<UserState>;
  popper: boolean;
  dispatch: (action: ActionType<UserState>) => void;
}
const Form = (props: Props) => {
  const { user, form, popper, dispatch } = props;
  const { client } = useAppStore(useShallowEnhance(['client']));
  const {
    control,
    register,
    setValue,
    clearErrors,
    setError,
    formState: { errors },
  } = form;
  const { t } = useTranslation();
  const [birthday, setBirthday] = useState<string | undefined>();
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const datePickerWrapperRef = useRef<HTMLDivElement>(null);
  const maxDate = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  }, []);

  const minDate = useMemo(() => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 150);
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);
  const [checking, setChecking] = useState(false);

  const debouncedUsernameCheck = useDebounceFn(
    async (value: string) => {
      try {
        setChecking(true);
        const res = await client?.Api.common.usernameCheck({ username: value.trim() });
        if (res?.code === 1) {
          clearErrors('username');
          dispatch({
            type: 'username',
            payload: value,
          });
        } else {
          setError(
            'username',
            { message: res?.message || '', type: 'manual' },
            { shouldFocus: true }
          );
        }
      } catch (_) {
        setError(
          'username',
          { message: 'Username check failed', type: 'manual' },
          { shouldFocus: true }
        );
      } finally {
        setChecking(false);
      }
    },
    { wait: 500 }
  );
  useClickAway(
    (event) => {
      if (
        event.target instanceof HTMLElement &&
        event.target.closest('.react-datepicker__year-dropdown')
      ) {
        return;
      }
      setOpenDatePicker(false);
    },
    [datePickerWrapperRef]
  );

  useEffect(() => {
    setValue('first_name', get(user, 'first_name'));
    setValue('last_name', get(user, 'last_name'));
    setValue('username', get(user, 'username'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  useEffect(() => {
    const timer = setTimeout(() => {
      const length = dayjs(birthday).format('YYYY-MM-DD').length;
      const inputRef = datePickerWrapperRef.current?.getElementsByTagName('input')[0];
      if (length && inputRef) {
        inputRef?.setSelectionRange(length, length);
      }
    });
    return () => {
      clearTimeout(timer);
    };
  }, [birthday]);
  return (
    <Stack>
      <Card.Root bg="transparent" border="0">
        <Card.Header color="fg" fontSize="xs">
          <Card.Title>
            {t('buttons.edit')}&nbsp;{t('profile.name')}
          </Card.Title>
        </Card.Header>
        <Card.Body py="2">
          <Stack gap="0" px="2" bg="white" borderRadius="lg">
            <Field.Root invalid={!!errors.first_name}>
              <Input
                maxLength={32}
                background="white"
                border="none"
                {...register('first_name', {
                  required: {
                    value: true,
                    message: t('userOnboarding.firstNameNoEmpty'),
                  },
                  onChange: (e) => {
                    dispatch({
                      type: 'first_name',
                      payload: e.target.value,
                    });
                  },
                  validate: {
                    notEmpty: (value) => {
                      const trimmed = value?.trim();
                      return !!trimmed || t('userOnboarding.firstNameNoEmpty');
                    },
                  },
                })}
                autoComplete="off"
                fontSize="sm"
                placeholder={t('profile.firstName')}
                _placeholder={{ color: 'fg.subtle' }}
              />
            </Field.Root>
            <Separator />
            <Field.Root invalid={!!errors.last_name}>
              <Input
                maxLength={32}
                background="white"
                border="none"
                {...register('last_name', {
                  onChange: (e) => {
                    dispatch({
                      type: 'last_name',
                      payload: e.target.value,
                    });
                  },
                })}
                autoComplete="off"
                fontSize="sm"
                placeholder={t('profile.lastName')}
                _placeholder={{ color: 'fg.subtle' }}
              />
              <Field.ErrorText>{errors?.last_name?.message as string}</Field.ErrorText>
            </Field.Root>
          </Stack>
        </Card.Body>
      </Card.Root>
      <Card.Root bg="transparent" border="0">
        <Card.Header pt="0" color="fg" fontSize="xs">
          <Card.Title>
            {t('buttons.edit')}&nbsp;{t(`profile.username`)}
          </Card.Title>
        </Card.Header>
        <Card.Body py="2">
          <Stack gap="0" px="2" bg="white" borderRadius="lg">
            <Field.Root invalid={!!errors.username}>
              <Input
                maxLength={32}
                background="white"
                border="none"
                {...register('username', {
                  onChange: (e) => {
                    debouncedUsernameCheck.run(e.target.value);
                  },
                  validate: {
                    checkAvailability: () => {
                      const hasManualError = errors.username?.message;
                      if (hasManualError) {
                        return errors?.username?.message;
                      }
                      return true;
                    },
                  },
                })}
                autoComplete="off"
                placeholder={t('profile.enterUsername')}
                _placeholder={{ color: 'fg.subtle' }}
                defaultValue={user?.username}
              />
              {checking && (
                <Field.HelperText fontSize="xs" display="flex" alignItems="center" gap={2}>
                  <Spinner size="xs" />
                  <Text>{t('profile.checkingUsernameTips')}</Text>
                </Field.HelperText>
              )}
              <Field.ErrorText>{errors?.username?.message as string}</Field.ErrorText>
            </Field.Root>
          </Stack>
          <VStack gap="0" pt="2">
            <Text fontSize="xs" w="full" color={'fg.muted'}>
              {t('profile.usernameExtra')}
            </Text>
            <Text fontSize="xs" w="full" color={'fg.muted'}>
              {t('profile.usernameExtra2')}
            </Text>
          </VStack>
        </Card.Body>
      </Card.Root>
      <Card.Root bg="transparent" border="0">
        <Card.Header pt="0" color="fg" fontSize="xs">
          <Card.Title>{t('profile.bio')}</Card.Title>
        </Card.Header>
        <Card.Body py="2">
          <Stack gap="0" px="2" bg="white" borderRadius="lg">
            <Field.Root invalid={!!errors.bio}>
              <Controller
                control={control}
                name="bio"
                render={({ field }) => (
                  <InputSize
                    maxLength={300}
                    background="white"
                    border="none"
                    name={field.name}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      dispatch({
                        type: 'bio',
                        payload: value as string,
                      });
                    }}
                    fontSize="sm"
                    placeholder={t('profile.bioPlaceholder')}
                    _placeholder={{ color: 'fg.subtle' }}
                  />
                )}
              />

              <Field.ErrorText>{errors?.bio?.message as string}</Field.ErrorText>
            </Field.Root>
          </Stack>
        </Card.Body>
      </Card.Root>
      <Card.Root bg="transparent" border="0">
        <Card.Header color="fg" fontSize="xs">
          <Card.Title>{t('profile.basicInformation')}</Card.Title>
        </Card.Header>
        <Card.Body py="2">
          <Stack gap="0" px="2" bg="white" borderRadius="lg">
            <Flex
              justifyContent="space-between"
              py="3.5"
              px="3"
              gap="3"
              alignItems="center"
              cursor="pointer"
              fontWeight="medium"
            >
              <Text color="fg">{t('profile.birthday')}</Text>
              <Box>
                <Field.Root invalid={!!errors.birth_day}>
                  <Controller
                    control={control}
                    name="birth_day"
                    render={({ field }) => (
                      <Box
                        ref={datePickerWrapperRef}
                        css={{
                          '& .react-datepicker__input-container input': {
                            borderColor: 'transparent',
                            width: '120px',
                            height: '32px',
                            fontSize: 'sm',
                            color: 'brand',
                          },
                          '& .react-datepicker__input-container:hover input': {
                            borderColor: 'border',
                          },
                          '& .react-datepicker-popper': {
                            pos: 'fixed !important',
                            zIndex: popper ? '2000' : '-9999 !important',
                            opacity: popper ? 1 : 0,
                          },
                        }}
                      >
                        <DatePicker
                          open={openDatePicker}
                          selected={birthday ? new Date(+birthday) : undefined}
                          name={field.name}
                          tabIndex={-1}
                          placeholderText={t('profile.selectBirthday')}
                          customInput={<Input size="sm" />}
                          dateFormat="yyyy-MM-dd"
                          minDate={minDate}
                          maxDate={maxDate}
                          onInputClick={() => {
                            setOpenDatePicker(true);
                          }}
                          onChange={(val: Date | null) => {
                            const ms = val?.getTime();
                            dispatch({
                              type: 'birth_day',
                              payload: ms || -1,
                            });
                            setBirthday(ms?.toString());
                            field.onChange(ms || -1);
                            setOpenDatePicker(false);
                          }}
                          isClearable
                          autoComplete="off"
                          showYearDropdown
                          dateFormatCalendar="MMMM"
                          yearDropdownItemNumber={50}
                          scrollableYearDropdown
                          dropdownMode="scroll"
                        />
                      </Box>
                    )}
                  />

                  <Field.ErrorText>{errors?.birth_day?.message as string}</Field.ErrorText>
                </Field.Root>
              </Box>
            </Flex>
            <Separator />
            <Flex
              justifyContent="space-between"
              py="3.5"
              px="3"
              gap="3"
              alignItems="center"
              cursor="pointer"
              fontWeight="medium"
            >
              <Text color="fg">{t('profile.gender')}</Text>
              <Box
                css={{
                  '& .chakra-select__valueText > div': {
                    fontSize: 'sm',
                  },
                }}
              >
                <EditGender
                  user={user}
                  onChange={(val) => {
                    dispatch({
                      type: 'gender',
                      payload: val?.gender,
                    });
                  }}
                />
              </Box>
            </Flex>
          </Stack>
        </Card.Body>
      </Card.Root>
    </Stack>
  );
};
const MemoizedForm = memo(Form);
export { MemoizedForm as Form };
